import * as React from 'react';
import { Grid, type GridProps } from './Layout';
import { Loading } from './Spinner';
import { StatCard } from './StatCard';

export interface StatGridItem {
  /** Plain text: it also names the spinner while the number loads. */
  label: string;
  /** Already formatted — the tile does no arithmetic. */
  value: React.ReactNode;
  loading?: boolean;
}

export interface StatGridProps extends Omit<GridProps, 'children'> {
  stats: StatGridItem[];
}

/**
 * A row of headline numbers, one StatCard each.
 *
 *   <StatGrid stats={[
 *     { label: 'Calls in progress', value: 4, loading },
 *     { label: 'Active impounds', value: 12 },
 *   ]} />
 *
 * A tile that is loading shows a spinner in its value slot, named after the
 * label so a screen reader hears "Loading calls in progress" rather than
 * three identical spinners. An empty value reads as 0.
 */
export const StatGrid = React.forwardRef<HTMLDivElement, StatGridProps>(
  function StatGrid(
    { stats, cols = { base: 1, sm: 2, md: 3 }, gap = 4, ...props },
    ref
  ) {
    return (
      <Grid ref={ref} cols={cols} gap={gap} {...props}>
        {stats.map((stat) => (
          <StatCard
            key={stat.label}
            label={stat.label}
            value={
              stat.loading ? (
                <Loading
                  label={`Loading ${stat.label.toLowerCase()}`}
                  pad="none"
                />
              ) : stat.value === null || stat.value === undefined ? (
                '0'
              ) : (
                stat.value
              )
            }
          />
        ))}
      </Grid>
    );
  }
);
